import type { FC, ReactNode } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from './cn.js';

/*
 * Модальное окно. Поведение - Radix Dialog (фокус внутри окна, Esc, клик по
 * подложке, блокировка прокрутки, aria-связь заголовка), вид - .modal макета:
 * шапка с заголовком, прокручиваемое тело, подвал с действиями.
 *
 * Radix взят адресно, только этот пакет (docs/design-system.md): остальные
 * примитивы на нём не держатся.
 */

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Подпись под заголовком; без неё Radix просит aria-describedby - отключаем явно. */
  description?: ReactNode;
  footer?: ReactNode;
  /** Класс ширины окна; по умолчанию как у .modal макета. */
  widthClassName?: string;
  children: ReactNode;
}

export const Dialog: FC<DialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  footer,
  widthClassName = 'w-[min(540px,90vw)]',
  children,
}) => (
  <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 z-[1000] bg-brand-navy/35 backdrop-blur-[4px]" />
      <RadixDialog.Content
        {...(description === undefined
          ? { 'aria-describedby': undefined }
          : null)}
        className={cn(
          'fixed top-1/2 left-1/2 z-[1001] flex max-h-[88vh] -translate-x-1/2 -translate-y-1/2 flex-col',
          'rounded-xl bg-white shadow-[0_12px_32px_rgba(15,23,34,0.18)] focus:outline-none',
          widthClassName,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-ink-150 px-[22px] py-[18px]">
          <div>
            <RadixDialog.Title className="m-0 text-base font-semibold text-ink-900">
              {title}
            </RadixDialog.Title>
            {description !== undefined && (
              <RadixDialog.Description className="mt-1 text-[12.5px] text-ink-500">
                {description}
              </RadixDialog.Description>
            )}
          </div>
          <RadixDialog.Close
            aria-label="Закрыть"
            className="grid size-7 shrink-0 cursor-pointer place-items-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X aria-hidden className="size-4" />
          </RadixDialog.Close>
        </div>

        <div className="overflow-y-auto px-[22px] py-5 text-[13.5px] leading-[1.55] text-ink-700">
          {children}
        </div>

        {footer !== undefined && (
          <div className="flex justify-end gap-2 border-t border-ink-150 px-[22px] py-3.5">
            {footer}
          </div>
        )}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  </RadixDialog.Root>
);
